import React, { useEffect } from 'react';
import { useIntl } from 'react-intl';

import { RolesSelect } from '~/components/Shared/RolesSelect/RolesSelect';
import { FormField } from '~/eds/src/components/FormField/FormField';
import { RoleType } from '~/enums/Roles';
import { FlagType } from '~/flags/flags';
import { useFlag } from '~/flags/useFlag';
import { api } from '~/redux/api/createApi';
import { FormField as FormFieldType, SingleSelectField } from '~/types/SSOForm';

type Props = {
  clientId?: string;
  formField: FormFieldType<SingleSelectField>;
  errors?: string[];
  onExplain?: (fieldName: string) => void;
  onChange: (value: string) => void;
};

export const RolesSelectField = ({
  clientId,
  formField,
  errors = [],
  onExplain,
  onChange,
}: Props) => {
  const isPremiumAdministrationEnabled = useFlag(
    FlagType.PremiumAdministration,
  );
  const { data: roles } = api.endpoints.getRolesSimple.useQuery(
    {
      page: 1,
      pageSize: 100,
      client_id: clientId,
    },
    { skip: isPremiumAdministrationEnabled },
  );

  const intl = useIntl();

  useEffect(() => {
    if (roles) {
      const powerUserRole = roles.results.find(
        (role) =>
          role.roleType === RoleType.PreBuiltRole &&
          role.name.toLowerCase() === 'power user',
      );
      if (powerUserRole) {
        onChange(powerUserRole.id);
      }
    }
  }, [roles]);

  return (
    <FormField
      label={formField.title}
      description={formField.description}
      onChange={(value) => {
        onChange(value as unknown as string);
      }}
      input={RolesSelect}
      onExplain={onExplain ? () => onExplain(formField.fieldName) : undefined}
      inputProps={{
        endpoint: formField.field.endpoint,
        clientId,
      }}
      disabled={!isPremiumAdministrationEnabled}
      tooltip={
        isPremiumAdministrationEnabled
          ? undefined
          : intl.formatMessage({
              id: 'RolesSelectField.premiumFeatureTooltip',
              defaultMessage:
                'Available with the Advanced Administration add-on.',
              description: 'Tooltip for premium features',
            })
      }
      error={errors.join(', ')}
      name={formField.fieldName}
      value={formField?.field?.value}
    />
  );
};
